import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";

const tagColors = {
  "One of One": "#00c9ff",
  Limited: "orange",
  Rare: "#B400FF",
  "Cash Flow": "#42D010",
};

const VehicleCard = ({
  carID,
  carName,
  tags = [],
  image,
  totalReceived,
  totalValue,
  location,
  progress,
  userInvestment,
}) => {
  const [totalPayouts, setTotalPayouts] = useState(0);

  // Generate a random delay for animation start times
  const randomDelay = React.useMemo(() => Math.random() * 30, []);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = `
      @keyframes pulseAnimation {
        0%, 100% {
          filter: brightness(1);
          background-position: 0% 0%;
        }
        50% {
          filter: brightness(1.5);
          background-position: 100% 100%;
        }
      }
      @keyframes textPopAnimation {
        0%, 45%, 55%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  useEffect(() => {
    const fetchPayouts = async () => {
      try {
        const payoutsQuery = query(
          collection(db, "payouts"),
          where("carID", "==", carID)
        );
        const payoutsSnapshot = await getDocs(payoutsQuery);
        let sum = 0;
        payoutsSnapshot.forEach((doc) => {
          const data = doc.data();
          sum += data.amount || 0;
        });
        setTotalPayouts(sum);
      } catch (error) {
        console.error("Error fetching payouts:", error);
      }
    };

    if (carID) {
      fetchPayouts();
    }
  }, [carID]);

  const roi =
    totalValue && totalValue > 0
      ? ((totalPayouts / totalValue) * 100).toFixed(2)
      : "N/A";

  // Determine the primary tag color for the neon effect
  const mainTag = tags.find((tag) => tagColors[tag]);
  const overlayColor = mainTag ? tagColors[mainTag] : null;

  const cardStyle = {
    position: "relative",
    border: "1px solid #333",
    borderRadius: "12px",
    padding: "12px",
    margin: "15px",
    width: "400px",
    background: "linear-gradient(135deg,rgb(20, 20, 20),rgb(55, 57, 61))",
    color: "#fff",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.6)",
    fontFamily: "Arial, sans-serif",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const neonOverlayStyle = {
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background: overlayColor
      ? `linear-gradient(
          135deg,
          transparent 48%,
          #fff 49%,
          ${overlayColor} 50%,
          #fff 51%,
          transparent 52%
        )`
      : "none",
    backgroundSize: "200% 200%",
    pointerEvents: "none",
    animation: overlayColor
      ? `pulseAnimation 30s ease-in-out ${randomDelay}s infinite`
      : "none",
    opacity: 0.6,
    zIndex: 2,
  };

  const contentWrapperStyle = {
    position: "relative",
    zIndex: 1,
    animation: overlayColor ? "textPopAnimation 60s infinite" : "none",
  };

  const imageContainerStyle = {
    position: "relative",
    width: "100%",
    height: "200px",
    overflow: "hidden",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "20px",
    boxShadow: "inset 0 0 10px rgba(0,0,0,0.5)",
  };

  const imageStyle = {
    width: "140%",
    height: "100%",
    objectFit: "fill",
    borderRadius: "10px",
  };

  const buttonContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "auto",
    gap: "10px",
    width: "100%",
  };

  // Updated Invest button with a linear gradient
  const investButtonStyle = {
    background: "linear-gradient(to right,rgb(46, 153, 10),rgb(101, 231, 9))", // <-- GRADIENT
    color: "#fff",
    border: "none",
    padding: "15px 60px",
    borderRadius: "8px",
    cursor: "pointer",
    marginLeft: "10px",
    marginTop: "5px",
    flex: 1,
    fontSize: "1.3em",
    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.6), 0 2px 2px rgba(0,0,0,0.4)",
  };

  const rentButtonStyle = {
    backgroundColor: "#18A0FF",
    color: "#fff",
    border: "none",
    padding: "15px 65px",
    borderRadius: "8px",
    cursor: "pointer",
    marginRight: "10px",
    marginTop: "5px",
    flex: 1,
    fontSize: "1.3em",
  };

  return (
    <div style={cardStyle}>
      {/* Neon Overlay (only if there's a valid tag) */}
      {overlayColor && <div style={neonOverlayStyle}></div>}

      <Link
        to={`/cars/${carID}`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <div style={contentWrapperStyle}>
          {/* Title, Tags, and Location */}
          <div style={{ margin: "2px 0", textAlign: "center" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "5px",
                flexWrap: "wrap",
              }}
            >
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  margin: "0",
                }}
              >
                {carName}
              </h3>
              <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                {tags.map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: "inline-block",
                      padding: "5px 10px",
                      backgroundColor: tagColors[tag] || "#666",
                      color: "#fff",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <p
              style={{
                fontSize: "14px",
                color: "#aaa",
                margin: "5px 0 10px 0",
              }}
            >
              {location}
            </p>
          </div>

          {/* Image */}
          <div style={imageContainerStyle}>
            <img
              src={image || "https://via.placeholder.com/350x200"}
              alt={carName}
              style={imageStyle}
            />
          </div>

          {/* Progress Bar */}
          <div
            style={{
              position: "relative",
              backgroundColor: progress >= 100 ? "#007bff" : "#333",
              borderRadius: "5px",
              overflow: "hidden",
              height: "20px",
              marginTop: "2px",
              boxShadow: "inset 0 0px 2px rgba(0,0,0,0.6)",
            }}
          >
            <div
              style={{
                backgroundColor: progress >= 100 ? "#007bff" : "#32B704",
                height: "100%",
                width: `${progress}%`,
              }}
            ></div>
            <span
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                textAlign: "center",
                fontSize: "14px",
                color: "#fff",
                lineHeight: "20px",
                zIndex: 10,
                pointerEvents: "none",
              }}
            >
              {progress.toFixed(1)}%
            </span>
          </div>

          {/* Total Value */}
          <div
            style={{
              textAlign: "center",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            <span
              style={{
                fontSize: "30px",
                fontWeight: "bold",
                color: "#fff",
              }}
            >
              ${totalValue?.toLocaleString() || "0"}
            </span>
            <br />
            <span
              style={{
                fontSize: "14px",
                color: "#aaa",
              }}
            >
              Total Value USD
            </span>
          </div>

          {/* Total Payouts and ROI */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              margin: "5px 0",
            }}
          >
            <span>
              <strong>Total Payouts: </strong>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#32B704",
                }}
              >
                ${Math.round(totalPayouts).toLocaleString()}
              </span>
            </span>
            <span>
              <strong>ROI: </strong>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#37DB01",
                }}
              >
                {roi !== "N/A" ? roi + "%" : "N/A"}
              </span>
            </span>
          </div>

          <p
            style={{
              margin: "5px 0",
              color: "#fff",
              textAlign: "left",
            }}
          >
            <strong>You own:</strong>{" "}
            <span
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#32B704",
              }}
            >
              ${userInvestment?.toLocaleString() || "0"}
            </span>
          </p>
        </div>
      </Link>

      {/* Action Buttons */}
      <div style={buttonContainerStyle}>
        {progress < 100 ? (
          <Link
            to={`/cars/${carID}`}
            style={{
              textDecoration: "none",
              flex: 1,
              textAlign: "center",
              marginBottom: "5px",
            }}
          >
            {/* Invest Button with Linear Gradient */}
            <button style={investButtonStyle}>Invest</button>
          </Link>
        ) : (
          <div style={{ display: "flex", gap: "10px", width: "100%" }}>
            <Link
              to={`/cars/${carID}`}
              style={{
                textDecoration: "none",
                flex: 1,
                textAlign: "center",
                marginBottom: "5px",
              }}
            >
              <button style={investButtonStyle}>Details</button>
            </Link>
            <Link
              to={`/rent/${carID}`}
              style={{
                textDecoration: "none",
                flex: 1,
                textAlign: "center",
                marginBottom: "5px",
              }}
            >
              <button style={rentButtonStyle}>Rent</button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleCard;
